import React, { useState, useEffect } from 'react';
import { Bot } from 'lucide-react';
import { GenieAvatar } from '@/components/GenieAvatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import StatCard from '@/pages/Dashboard/components/StatCard';
import { useAgentAnalytics, useAnalyticsOverview, useMultipleAgentTimelines } from '@/hooks/analytics/useAnalytics';
import { DEFAULT_BRANDING } from '@/types/ui/branding';
import ConversationTimelineChart from './components/ConversationTimelineChart';
import DateRangePicker, { DateRange } from './components/DateRangePicker';
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const Analytics = ({ embedded = false }: { embedded?: boolean } = {}) => {
  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    to: new Date(),
  });

  const { data: agentAnalytics = [], isLoading } = useAgentAnalytics();
  const { data: overview = { totalConversations: 0, totalAgents: 0, averageConversationsPerAgent: 0, topPerformingAgent: null } } = useAnalyticsOverview();
  const { data: agentTimelines = [], isLoading: timelineLoading } = useMultipleAgentTimelines(
    selectedAgents,
    dateRange.from,
    dateRange.to,
  );

  useEffect(() => {
    if (!isLoading && selectedAgents.length === 0 && agentAnalytics.length > 0) {
      const agentsWithConversations = agentAnalytics.filter(agent => agent.conversationCount > 0);
      setSelectedAgents(agentsWithConversations.map(agent => agent.id));
    }
  }, [isLoading, agentAnalytics]);

  const handleAgentSelection = (agentId: string, checked: boolean) => {
    if (checked) {
      setSelectedAgents((prev) => [...prev, agentId]);
    } else {
      setSelectedAgents((prev) => prev.filter((id) => id !== agentId));
    }
  };

  const handleSelectAll = () => {
    // Filter out agents with zero conversations
    const agentsWithConversations = agentAnalytics.filter(agent => agent.conversationCount > 0);
    
    if (selectedAgents.length === agentsWithConversations.length) {
      setSelectedAgents([]);
    } else {
      setSelectedAgents(agentsWithConversations.map((agent) => agent.id));
    }
  };

  const overviewCards = [
    {
      icon: <GenieIcon name="chat-check" size={32} label="Total Conversations" className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10" />,
      label: "Total Conversations",
      getValue: () => overview.totalConversations,
      iconBgClass: "bg-transparent",
      iconTextClass: "",
    },
    {
      icon: <GenieIcon name="users" size={32} label="Active Genies" className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10" />,
      label: "Active Genies",
      getValue: () => overview.totalAgents,
      iconBgClass: "bg-transparent",
      iconTextClass: "",
    },
    {
      icon: <GenieIcon name="target" size={32} label="Avg. Conversations per Genie" className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10" />,
      label: "Avg. Conversations/Genie",
      getValue: () => overview.averageConversationsPerAgent,
      iconBgClass: "bg-transparent",
      iconTextClass: "",
    },
    {
      icon: <GenieIcon name="user" size={32} label="Top Performing Genie" className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10" />,
      label: "Top Performing Genie",
      getValue: () => overview.topPerformingAgent || "N/A",
      iconBgClass: "bg-transparent",
      iconTextClass: "",
    },
  ];

  const mainContent = (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-4 sm:p-6 space-y-6 sm:space-y-8">
              {/* Overview Stats */}
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-[#222B45] mb-3 sm:mb-4">Overview</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                  {overviewCards.map((card, index) => (
                    <StatCard
                      key={index}
                      icon={card.icon}
                      label={card.label}
                      value={card.getValue()}
                      isLoading={isLoading}
                      iconBgClass={card.iconBgClass}
                      iconTextClass={card.iconTextClass}
                    />
                  ))}
                </div>
              </div>

              {/* Conversation Timeline */}
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4">
                  <h2 className="text-lg sm:text-xl font-semibold text-[#222B45]">Conversation Timeline</h2>
                  <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
                </div>

                <div className="bg-[#F9FAFC] p-3 sm:p-6 rounded-xl border border-[#E5EAF0] mb-4 sm:mb-6 h-[500px]">
                  <ConversationTimelineChart timelines={agentTimelines} isLoading={timelineLoading} />
                </div>
              </div>

              {/* Genie Performance */}
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4">
                  <h2 className="text-lg sm:text-xl font-semibold text-[#222B45]">Genie Performance</h2>
                  {agentAnalytics.length > 0 && (
                    <Button variant="outline" size="sm" onClick={handleSelectAll} className="self-start sm:self-auto">
                      {(() => {
                        const agentsWithConversations = agentAnalytics.filter(agent => agent.conversationCount > 0);
                        return selectedAgents.length === agentsWithConversations.length && agentsWithConversations.length > 0 ? "Deselect All" : "Select All Active";
                      })()}
                    </Button>
                  )}
                </div>

                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-genie-primary"></div>
                  </div>
                ) : agentAnalytics.length === 0 ? (
                  <div className="text-center py-12">
                    <Bot className="h-12 w-12 text-[#64748B] mx-auto mb-4" />
                    <p className="text-[#64748B] text-lg">No genies found</p>
                    <p className="text-[#64748B] text-sm">Create your first genie to start seeing analytics</p>
                  </div>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {agentAnalytics.map((agent, index) => {
                      const primaryColor = agent.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
                      const secondaryColor = agent.branding?.secondaryColor;
                      const customAvatarUrl = agent.branding?.customAvatarUrl;
                      const gradientEnabled = agent.branding?.gradientEnabled;
                      const gradientAngle = agent.branding?.gradientAngle;
                      
                      return (
                        <div 
                          key={agent.id} 
                          className={`bg-[#F9FAFC] p-4 sm:p-6 rounded-xl border-2 transition-all cursor-pointer hover:shadow-md ${
                            selectedAgents.includes(agent.id) 
                              ? 'bg-opacity-10' 
                              : 'hover:border-gray-300'
                          }`}
                          style={{
                            borderColor: selectedAgents.includes(agent.id) ? primaryColor : '#E5EAF0',
                            backgroundColor: selectedAgents.includes(agent.id) ? `${primaryColor}10` : '#F9FAFC'
                          }}
                          onClick={() => handleAgentSelection(agent.id, !selectedAgents.includes(agent.id))}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center">
                              <Checkbox
                                checked={selectedAgents.includes(agent.id)}
                                onCheckedChange={(checked) => handleAgentSelection(agent.id, !!checked)}
                                onClick={(e) => e.stopPropagation()}
                                className="mr-3 sm:mr-4 flex-shrink-0"
                              />
                              <div className="mr-3 sm:mr-4 flex-shrink-0">
                                <GenieAvatar 
                                  color={primaryColor}
                                  secondaryColor={secondaryColor}
                                  customAvatarUrl={customAvatarUrl}
                                  gradientEnabled={gradientEnabled}
                                  gradientAngle={gradientAngle}
                                  size={80}
                                />
                              </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-base sm:text-lg font-semibold text-[#222B45] truncate">{agent.name}</h3>
                                {agent.isTeamAgent && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700 flex-shrink-0">
                                    Team
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                <p className="text-xs sm:text-sm text-[#64748B]">#{index + 1} performing genie</p>
                                {agent.owner.name && (
                                  <div className="flex items-center gap-1">
                                    <span className="hidden sm:inline text-[#64748B]">•</span>
                                    <span className="text-xs text-[#64748B]">
                                      by {agent.owner.name}
                                    </span>
                                  </div>
                                )}
                                {agent.brand && (
                                  <div className="flex items-center gap-1">
                                    <span className="hidden sm:inline text-[#64748B]">•</span>
                                    <span className="text-xs text-[#10B981] font-medium">
                                      {agent.brand}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-8 ml-11 sm:ml-0">
                            <div className="text-center sm:text-right">
                              <p className="text-xs sm:text-sm text-[#64748B]">Total Conversations</p>
                              <p className="text-lg sm:text-2xl font-bold text-[#222B45]">{agent.conversationCount}</p>
                            </div>
                            <div className="text-center sm:text-right">
                              <p className="text-xs sm:text-sm text-[#64748B]">Last 7 Days</p>
                              <p className="text-lg sm:text-2xl font-bold text-[#10B981]">{agent.recentConversations}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                    })}

                    {selectedAgents.length > 0 && (
                      <div className="text-center text-sm text-[#64748B] mt-4">
                        {selectedAgents.length} genie{selectedAgents.length !== 1 ? "s" : ""} selected for timeline view
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
  );

  if (embedded) {
    return mainContent;
  }

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-8 max-w-[1920px]">
        <div className="w-full">
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center">
              <GenieIcon name="trending-up" size={28} label="Analytics" className="h-7 w-7 md:h-8 md:w-8 mr-3 md:mr-4" />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-[#222B45]">Analytics</h1>
              </div>
            </div>
          </div>
          {mainContent}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
